import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { FileText, Plus, Trash2, ArrowLeft, Save, Printer, Download, Send, X, Check, Ban, Upload, AlertTriangle, UserPlus } from 'lucide-react';
import CustomerDedupHints from '../../components/CustomerDedupHints';
import QuickAddCustomerButton from '../../components/QuickAddCustomerDialog';
import { toast } from 'sonner';
import { DocHistoryPanel } from '../../components/orders/DocHistoryPanel';
import { DocOverrideActions } from '../../components/orders/DocOverrideActions';
import { SearchPicker, customerOptions } from '../../components/SearchPicker';
import { ProductKitDialog, hasKit } from '../../components/products/ProductKitDialog';
import DocTemplatePicker from '../../components/DocTemplatePicker';
import DocumentChainStrip from '../../components/DocumentChainStrip';
import LockedDocBanner from '../../components/LockedDocBanner';
import SoPaymentsCard from '../../components/orders/SoPaymentsCard';

const STATUS_META = {
  Draft:     { bg: 'bg-slate-100 text-slate-700 border-slate-200' },
  Issued:    { bg: 'bg-blue-100 text-blue-800 border-blue-200' },
  Fulfilled: { bg: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  Closed:    { bg: 'bg-slate-900 text-white border-slate-900' },
  Canceled:  { bg: 'bg-red-100 text-red-800 border-red-200' },
};

const newLine = () => ({ id: `tmp-${Math.random().toString(36).slice(2)}`, item_sku: '', bom_id: '', product_type_id: '', product_name: '', description: '', quantity: 1, uom: '', unit_price: 0, discount_pct: 0, taxable: true, service_date: '', class_name: '', notes: '' });

const SalesOrderDetailPage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const tplId = searchParams.get('tpl');
  const dupId = searchParams.get('dup');
  const isNew = !id || id === 'new';
  const [so, setSo] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [kitFor, setKitFor] = useState(null);
  const [form, setForm] = useState({
    customer_id: '', customer_name: '', invoice_number: '',
    order_date: new Date().toISOString().slice(0, 10),
    due_date: '', requested_ship_date: '', po_reference: '', tracking_number: '',
    tax_rate: 0, notes: '',
    payment_terms: '', shipping_method: '', salesperson_name: '', incoterms: '',
    invoice_level_discount_pct: 0, invoice_level_discount_amount: 0, deposit_amount: 0,
    message_on_invoice: '', memo: '',
    lines: [newLine()],
  });
  const [saving, setSaving] = useState(false);
  const [acting, setActing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [invoiceDupes, setInvoiceDupes] = useState([]);     // iter102 — soft duplicate-invoice# warning
  const [promoting, setPromoting] = useState(false);          // iter102 — "Save walk-in to master list"

  const load = useCallback(async () => {
    try {
      const [c, it, bm] = await Promise.all([
        api().get('/customers'),
        api().get('/items'),
        api().get('/product-types?level=product'),
      ]);
      setCustomers(c.data || []); setItems(it.data || []); setProducts((bm.data || []).filter(p => p.active_status !== false));
      if (!isNew || dupId) {
        const s = await api().get(`/sales-orders/${isNew ? dupId : id}`);
        if (isNew) toast.success(`Copied from ${s.data.so_number}. Review, then click Create Draft`);
        setSo(isNew ? null : s.data);
        setForm({
          customer_id: s.data.customer_id || '',
          customer_name: s.data.customer_id ? '' : (s.data.customer_name_snapshot || ''),
          invoice_number: isNew ? '' : (s.data.invoice_number || ''),
          order_date: isNew ? new Date().toISOString().slice(0, 10) : s.data.order_date,
          due_date: isNew ? '' : (s.data.due_date || ''),
          requested_ship_date: s.data.requested_ship_date || '',
          po_reference: s.data.po_reference || '',
          tracking_number: s.data.tracking_number || '',
          tax_rate: s.data.tax_rate || 0,
          notes: s.data.notes || '',
          payment_terms: s.data.payment_terms || '',
          shipping_method: s.data.shipping_method || '',
          salesperson_name: s.data.salesperson_name || '',
          incoterms: s.data.incoterms || '',
          invoice_level_discount_pct: s.data.invoice_level_discount_pct || 0,
          invoice_level_discount_amount: s.data.invoice_level_discount_amount || 0,
          deposit_amount: s.data.deposit_amount || 0,
          message_on_invoice: s.data.message_on_invoice || '',
          memo: s.data.memo || '',
          lines: (s.data.lines || []).map(l => ({ taxable: true, service_date: '', class_name: '', discount_pct: 0, ...l })),
        });
      } else if (tplId) {
        // iter96 — pre-fill a new SO from the ?tpl=<id> template
        try {
          const list = await api().get('/doc-templates?doc_type=sales_order');
          const t = (list.data || []).find(x => x.id === tplId);
          if (t) {
            const d = t.template_data || {};
            setForm(prev => ({
              ...prev,
              customer_id: d.customer_id ?? prev.customer_id,
              po_reference: d.po_reference ?? prev.po_reference,
              payment_terms: d.payment_terms ?? prev.payment_terms,
              shipping_method: d.shipping_method ?? prev.shipping_method,
              salesperson_name: d.salesperson_name ?? prev.salesperson_name,
              incoterms: d.incoterms ?? prev.incoterms,
              tax_rate: d.tax_rate ?? prev.tax_rate,
            }));
            toast.success(`Loaded template "${t.name}"`);
          }
        } catch { /* non-blocking */ }
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Load failed'); }
  }, [api, id, isNew, tplId, dupId]);
  useEffect(() => { load(); }, [load]);

  // iter102 — Debounced invoice-number duplicate check (soft warning only)
  useEffect(() => {
    const inv = (form.invoice_number || '').trim();
    if (!inv) { setInvoiceDupes([]); return; }
    const t = setTimeout(async () => {
      try {
        const params = new URLSearchParams({ invoice_number: inv });
        if (!isNew && id) params.append('exclude_id', id);
        const r = await api().get(`/sales-orders-invoice-check?${params.toString()}`);
        setInvoiceDupes(r.data?.duplicates || []);
      } catch { /* non-blocking */ }
    }, 400);
    return () => clearTimeout(t);
  }, [form.invoice_number, id, isNew, api]);

  // iter102 — Promote a freeform (walk-in) customer_name to a real Customer row
  const promoteCustomer = async () => {
    if (!so || so.customer_id) return;
    setPromoting(true);
    try {
      const r = await api().post(`/sales-orders/${id}/promote-customer`);
      setSo(r.data);
      // Refresh the customer dropdown
      const c = await api().get('/customers');
      setCustomers(c.data || []);
      setForm(prev => ({ ...prev, customer_id: r.data.customer_id, customer_name: '' }));
      toast.success(`"${r.data.customer_name_snapshot}" saved to master list`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Promote failed'); }
    finally { setPromoting(false); }
  };

  const editable = isNew || (so && (so.status === 'Draft' || so.status === 'Issued'));
  // iter98 — QB-style totals math (parity with backend _recompute_totals)
  const subtotal = (form.lines || []).reduce((acc, ln) => acc + (Number(ln.quantity || 0) * Number(ln.unit_price || 0) * (1 - Number(ln.discount_pct || 0) / 100)), 0);
  const taxableRaw = (form.lines || []).reduce((acc, ln) => (ln.taxable === false ? acc : acc + (Number(ln.quantity || 0) * Number(ln.unit_price || 0) * (1 - Number(ln.discount_pct || 0) / 100))), 0);
  const discountAmount = Math.min(subtotal, Number(form.invoice_level_discount_amount || 0) > 0 ? Number(form.invoice_level_discount_amount) : subtotal * Number(form.invoice_level_discount_pct || 0) / 100);
  const taxableAfter = subtotal > 0 && discountAmount > 0 ? taxableRaw * (1 - discountAmount / subtotal) : taxableRaw;
  const taxAmount = taxableAfter * Number(form.tax_rate || 0);
  const total = subtotal - discountAmount + taxAmount;
  const paymentsTotal = Number(so?.payments_total || 0);
  const balanceDue = total - Number(form.deposit_amount || 0) - paymentsTotal;

  const uploadInvoice = async (e) => {
    const f = e.target?.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const r = await api().post('/sales-orders/parse-upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const d = r.data || {};
      // Try to match imported customer by name
      let matchedId = form.customer_id;
      if (d.customer_name) {
        const hit = customers.find(c => (c.customer_name || '').toLowerCase() === d.customer_name.toLowerCase());
        if (hit) matchedId = hit.id;
      }
      setForm(prev => ({
        ...prev,
        customer_id: matchedId,
        customer_name: matchedId ? '' : (d.customer_name || prev.customer_name),
        invoice_number: d.invoice_number || prev.invoice_number,
        order_date: d.order_date || prev.order_date,
        due_date: d.due_date || prev.due_date,
        payment_terms: d.payment_terms || prev.payment_terms,
        shipping_method: d.shipping_method || prev.shipping_method,
        po_reference: d.po_reference || prev.po_reference,
        tracking_number: d.tracking_number || prev.tracking_number,
        salesperson_name: d.salesperson_name || prev.salesperson_name,
        message_on_invoice: d.message_on_invoice || prev.message_on_invoice,
        memo: d.memo || prev.memo,
        tax_rate: d.tax_rate != null ? d.tax_rate : prev.tax_rate,
        invoice_level_discount_amount: d.invoice_level_discount_amount != null ? d.invoice_level_discount_amount : prev.invoice_level_discount_amount,
        deposit_amount: d.deposit_amount != null ? d.deposit_amount : prev.deposit_amount,
        lines: (d.lines && d.lines.length) ? d.lines.map(ln => ({ id: `tmp-${Math.random().toString(36).slice(2)}`, item_sku: '', bom_id: '', description: ln.description || '', quantity: Number(ln.quantity || 0), unit_price: Number(ln.unit_price || 0), uom: ln.uom || '', discount_pct: 0, taxable: ln.taxable !== false, service_date: ln.service_date || '', class_name: ln.class_name || '', notes: '' })) : prev.lines,
      }));
      const parts = [];
      if (d.customer_name) parts.push(matchedId !== form.customer_id ? `Matched "${d.customer_name}"` : `Customer "${d.customer_name}" (unmatched — pick one)`);
      parts.push(`${(d.lines || []).length} line item(s)`);
      toast.success(`Parsed from ${d.source || 'file'}: ${parts.join(' · ')}`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Parse failed'); }
    finally { setUploading(false); if (e.target) e.target.value = ''; }
  };

  const addLine = () => setForm({ ...form, lines: [...form.lines, newLine()] });
  const removeLine = (i) => setForm({ ...form, lines: form.lines.filter((_, idx) => idx !== i) });
  const updateLine = (i, patch) => setForm({ ...form, lines: form.lines.map((ln, idx) => idx === i ? { ...ln, ...patch } : ln) });

  const priceOr = (price, i) => (price != null && price !== '' ? Number(price) : form.lines[i].unit_price);
  const fillLineFromItem = (i, sku) => {
    if (!sku) { updateLine(i, { item_sku: '' }); return; }
    const it = items.find(x => x.item_sku === sku);
    if (!it) return;
    updateLine(i, { item_sku: it.item_sku, bom_id: '', product_type_id: '', product_name: '', product_sku: '', description: it.item_name, uom: it.base_uom || '', unit_price: priceOr(it.sales_price, i) });
  };
  const fillLineFromProduct = (i, pid) => {
    if (!pid) { updateLine(i, { product_type_id: '', product_name: '', product_sku: '', bom_id: '' }); return; }
    const p = products.find(x => x.id === pid);
    if (!p) return;
    updateLine(i, { product_type_id: p.id, product_name: p.product_type_name, product_sku: p.product_sku || '', bom_id: '', item_sku: '', description: p.description || p.product_type_name, uom: 'EA', unit_price: priceOr(p.sales_price, i) });
    if (hasKit(p)) setKitFor({ index: i, product: p, qty: Number(form.lines[i].quantity) || 1 });
  };
  const addKitLines = (rows) => {
    const { index, product } = kitFor;
    const added = rows.map(r => ({ ...newLine(), item_sku: r.item?.item_sku || r.sku, description: r.item ? r.item.item_name : r.sku, uom: r.item?.base_uom || 'EA', quantity: r.quantity, unit_price: r.bundled ? 0 : Number(r.item?.sales_price || 0), notes: `${r.group ? `${r.group} for` : 'Included with'} ${product.product_type_name}${r.bundled ? ' (bundle)' : ''}` }));
    setForm(f => ({ ...f, lines: [...f.lines.slice(0, index + 1), ...added, ...f.lines.slice(index + 1)] }));
    setKitFor(null);
    toast.success(`${added.length} accessory line(s) added`);
  };
  const itemOptions = items.filter(it => it.item_sku).map(it => ({ value: it.item_sku, label: `${it.item_sku} — ${it.item_name}`, sub: it.sales_price != null ? `$${Number(it.sales_price).toFixed(2)}` : '' }));
  const productOptions = products.map(p => ({ value: p.id, label: p.product_sku ? `${p.product_sku} — ${p.product_type_name}` : p.product_type_name, sub: p.sales_price != null ? `$${Number(p.sales_price).toFixed(2)}` : '' }));

  const save = async () => {
    if (!form.customer_id && !(form.customer_name || '').trim()) { toast.error('Pick a customer or type a customer name'); return; }
    const valid = (form.lines || []).filter(l => (l.description || '').trim());
    if (!valid.length) { toast.error('Add at least one line item'); return; }
    setSaving(true);
    try {
      const payload = {
        customer_id: form.customer_id || null,
        customer_name: form.customer_id ? null : (form.customer_name || '').trim() || null,
        invoice_number: (form.invoice_number || '').trim() || null,
        order_date: form.order_date,
        due_date: form.due_date || null,
        requested_ship_date: form.requested_ship_date || null,
        po_reference: form.po_reference || null,
        tracking_number: form.tracking_number || null,
        payment_terms: form.payment_terms || null,
        shipping_method: form.shipping_method || null,
        salesperson_name: form.salesperson_name || null,
        incoterms: form.incoterms || null,
        tax_rate: Number(form.tax_rate || 0),
        invoice_level_discount_pct: Number(form.invoice_level_discount_pct || 0),
        invoice_level_discount_amount: Number(form.invoice_level_discount_amount || 0),
        deposit_amount: Number(form.deposit_amount || 0),
        message_on_invoice: form.message_on_invoice || null,
        memo: form.memo || null,
        notes: form.notes || null,
        lines: valid.map(l => ({
          item_sku: l.item_sku || null, bom_id: l.bom_id || null,
          product_type_id: l.product_type_id || null, product_name: l.product_name || null, product_sku: l.product_sku || null,
          description: l.description, quantity: Number(l.quantity || 0),
          unit_price: Number(l.unit_price || 0), uom: l.uom || null,
          discount_pct: Number(l.discount_pct || 0),
          taxable: l.taxable !== false,
          service_date: l.service_date || null,
          class_name: l.class_name || null,
          notes: l.notes || null,
        })),
      };
      if (isNew) {
        const r = await api().post('/sales-orders', payload);
        toast.success(`${r.data.so_number} created as Draft`);
        navigate(`/orders/sales/${r.data.id}`, { replace: true });
      } else {
        const r = await api().put(`/sales-orders/${id}`, payload);
        setSo(r.data);
        toast.success(`${r.data.so_number} saved`);
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); } finally { setSaving(false); }
  };

  const act = async (action, extra = {}) => {
    setActing(true);
    try {
      const r = await api().post(`/sales-orders/${id}/${action}`, extra);
      setSo(r.data);
      toast.success(`${r.data.so_number} → ${r.data.status}`);
    } catch (e) { toast.error(e.response?.data?.detail || `${action} failed`); } finally { setActing(false); }
  };


  const printSo = () => {
    const base = api().defaults.baseURL;
    const token = api().defaults.headers?.Authorization;
    // Open in new window — pass token via fetch then write doc to the opened window
    const w = window.open('', '_blank');
    if (!w) { toast.error('Popup blocked'); return; }
    w.document.write('Loading…');
    fetch(`${base}/sales-orders/${id}/print`, { headers: { Authorization: token } })
      .then(r => r.text())
      .then(html => { w.document.open(); w.document.write(html); w.document.close(); })
      .catch(() => { w.close(); toast.error('Print load failed'); });
  };

  const download = async (fmt) => {
    try {
      const res = await api().get(fmt === 'pdf' ? `/sales-orders/${id}/pdf` : `/sales-orders/${id}/export.${fmt}`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: fmt === 'pdf' ? 'application/pdf' : (fmt === 'json' ? 'application/json' : 'text/csv') });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `${so.so_number}.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${fmt.toUpperCase()} downloaded`);
    } catch { toast.error('Download failed'); }
  };

  const emailSo = async () => {
    try {
      const r = await api().post(`/sales-orders/${id}/email`);
      toast.success(`Emailed to ${r.data.to}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Email failed'); }
  };

  const statusPill = so && (
    <Badge className={`${(STATUS_META[so.status] || STATUS_META.Draft).bg} border rounded-sm text-xs uppercase tracking-wider`} data-testid="so-status-pill">
      {so.status}
    </Badge>
  );

  return (
    <div className="space-y-4" data-testid="sales-order-detail-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/orders/sales')}><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <FileText className="w-6 h-6 text-slate-700" />
              {isNew ? 'New Sales Order' : so?.so_number || 'Loading…'}
              {statusPill}
              {so?.is_paid && <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 border rounded-sm text-xs" data-testid="so-paid-badge">PAID</Badge>}
            </h1>
            {so && <p className="text-xs text-slate-500 mt-1">Created {so.created_at?.slice(0, 10)} · {so.customer_name_snapshot}</p>}
          </div>
        </div>
        {so && (
          <div className="flex items-center gap-1 flex-wrap">
            <Button variant="outline" size="sm" onClick={printSo} className="rounded-sm" data-testid="so-print-btn"><Printer className="w-3.5 h-3.5 mr-1" /> Print</Button>
            <Button variant="outline" size="sm" onClick={() => download('pdf')} className="rounded-sm" data-testid="so-export-pdf-btn"><Download className="w-3.5 h-3.5 mr-1" /> PDF</Button>
            <Button variant="outline" size="sm" onClick={() => download('json')} className="rounded-sm" data-testid="so-export-json-btn"><Download className="w-3.5 h-3.5 mr-1" /> JSON</Button>
            <Button variant="outline" size="sm" onClick={() => download('csv')} className="rounded-sm" data-testid="so-export-csv-btn"><Download className="w-3.5 h-3.5 mr-1" /> CSV</Button>
            <Button variant="outline" size="sm" onClick={emailSo} className="rounded-sm" data-testid="so-email-btn"><Send className="w-3.5 h-3.5 mr-1" /> Email</Button>
            {so.status === 'Draft' && <Button size="sm" onClick={() => act('issue')} disabled={acting} className="bg-blue-600 hover:bg-blue-700 rounded-sm" data-testid="so-issue-btn"><Check className="w-3.5 h-3.5 mr-1" /> Issue</Button>}
            {so.status === 'Issued' && <Button size="sm" onClick={() => act('fulfill')} disabled={acting} className="bg-emerald-600 hover:bg-emerald-700 rounded-sm" data-testid="so-fulfill-btn"><Check className="w-3.5 h-3.5 mr-1" /> Fulfill</Button>}
            {so.status === 'Fulfilled' && <Button size="sm" onClick={() => act('close')} disabled={acting} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="so-close-btn"><Check className="w-3.5 h-3.5 mr-1" /> Close</Button>}
            <DocOverrideActions docType="sales-orders" docId={id} label="Sales Order" number={so.so_number} status={so.status} cancelable={['Draft', 'Issued', 'Fulfilled'].includes(so.status)} testPrefix="so" onAction={act} onReload={load} onDeleted={() => navigate('/orders/sales')} />
            {so.status === 'Issued' && <Button size="sm" variant="outline" onClick={() => navigate(`/orders/production/new?from_so=${so.id}`)} className="border-amber-300 text-amber-700 hover:bg-amber-50 rounded-sm" data-testid="so-spawn-po-btn">⇢ Spawn Production Order</Button>}
            {['Issued', 'Fulfilled'].includes(so.status) && <Button size="sm" variant="outline" onClick={() => navigate(`/orders/packing/new?from_so=${so.id}`)} className="border-sky-300 text-sky-700 hover:bg-sky-50 rounded-sm" data-testid="so-spawn-ps-btn">⇢ Create Packing Slip</Button>}
          </div>
        )}
      </div>

      {!isNew && <DocumentChainStrip api={api} docType="sales_order" docId={id} />}
      {so && !editable && <LockedDocBanner doc={so} label="Sales Order" onDuplicate={() => navigate(`/orders/sales/new?dup=${id}`)} />}

      {editable && (
        <DocTemplatePicker
          api={api}
          docType="sales_order"
          testIdPrefix="so-tpl"
          collectCurrent={() => ({
            customer_id: form.customer_id,
            po_reference: form.po_reference,
            payment_terms: form.payment_terms,
            shipping_method: form.shipping_method,
            salesperson_name: form.salesperson_name,
            incoterms: form.incoterms,
            tax_rate: form.tax_rate,
          })}
          onApply={(d) => setForm(prev => ({
            ...prev,
            customer_id: d.customer_id ?? prev.customer_id,
            po_reference: d.po_reference ?? prev.po_reference,
            payment_terms: d.payment_terms ?? prev.payment_terms,
            shipping_method: d.shipping_method ?? prev.shipping_method,
            salesperson_name: d.salesperson_name ?? prev.salesperson_name,
            incoterms: d.incoterms ?? prev.incoterms,
            tax_rate: d.tax_rate ?? prev.tax_rate,
          }))}
        />
      )}

      <Card className="industrial-card">
        <CardHeader className="border-b border-slate-100 pb-3 flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base">Header</CardTitle>
          {editable && isNew && (
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input type="file" accept=".pdf,.csv,.png,.jpg,.jpeg,.webp" className="hidden" onChange={uploadInvoice} data-testid="so-upload-input" disabled={uploading} />
              <span className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-amber-100 border border-amber-300 rounded-sm hover:bg-amber-200" data-testid="so-upload-btn">
                <Upload className="w-3.5 h-3.5" /> {uploading ? 'Parsing…' : 'Upload QB Invoice (PDF/CSV/Image)'}
              </span>
            </label>
          )}
        </CardHeader>
        <CardContent className="p-4 grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="col-span-2 md:col-span-2">
            <Label>Customer *</Label>
            <div className="flex gap-2">
              <div className="flex-1 min-w-0">
                <SearchPicker value={form.customer_id || ''} options={customerOptions(customers)} onChange={(v) => setForm({ ...form, customer_id: v || '', customer_name: '' })} disabled={!editable} placeholder="Search customers…" testId="so-customer-select" emptyText="No customer found — type a name or add new" />
              </div>
              <Input placeholder="…or type a name" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value, customer_id: '' })} disabled={!editable} className="rounded-sm flex-1" data-testid="so-customer-name" />
              <QuickAddCustomerButton api={api} disabled={!editable} testId="so-new-customer" onCreated={(c) => { setCustomers(prev => [...prev, c].sort((x, y) => (x.customer_name || '').localeCompare(y.customer_name || ''))); setForm(f => ({ ...f, customer_id: c.id, customer_name: '' })); }} />
            </div>
            <CustomerDedupHints
              api={api}
              name={form.customer_name}
              skip={!editable || !!form.customer_id}
              onPick={(c) => { setForm({ ...form, customer_id: c.id, customer_name: '' }); }}
              testIdPrefix="so-dedup"
            />
            <div className="flex items-center justify-between mt-1 gap-2 flex-wrap">
              <p className="text-[10px] text-slate-500">Pick an existing customer <strong>or</strong> type a name — either works.</p>
              {so && !so.customer_id && so.customer_name_snapshot && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={promoteCustomer}
                  disabled={promoting}
                  className="rounded-sm h-7 text-[11px] border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                  data-testid="so-promote-customer-btn"
                  title="Save this walk-in customer to the master list so repeat orders can match it next time"
                ><UserPlus className="w-3 h-3 mr-1" /> {promoting ? 'Saving…' : 'Save walk-in to master list'}</Button>
              )}
            </div>
          </div>
          <div>
            <Label>Invoice #</Label>
            <Input value={form.invoice_number} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} placeholder={so?.so_number ? `Auto: ${so.so_number}` : 'Auto if blank'} disabled={!editable} className={`rounded-sm mono ${invoiceDupes.length ? 'border-amber-400 bg-amber-50' : ''}`} data-testid="so-invoice-number" />
            {invoiceDupes.length > 0 && (
              <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-sm px-2 py-1 mt-1 flex items-start gap-1" data-testid="so-invoice-dupe-warning">
                <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                <div className="leading-tight">
                  Already used on {invoiceDupes.length} other SO{invoiceDupes.length > 1 ? 's' : ''}:
                  <span className="mono"> {invoiceDupes.slice(0, 3).map(d => d.so_number).join(', ')}{invoiceDupes.length > 3 ? `, +${invoiceDupes.length - 3}` : ''}</span>
                </div>
              </div>
            )}
          </div>
          <div><Label>Invoice Date</Label><Input type="date" value={form.order_date} onChange={(e) => setForm({ ...form, order_date: e.target.value })} disabled={!editable} className="rounded-sm" data-testid="so-order-date" /></div>
          <div><Label>Due Date</Label><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} disabled={!editable} className="rounded-sm" data-testid="so-due-date" /></div>
          <div><Label>Terms</Label><Input value={form.payment_terms} onChange={(e) => setForm({ ...form, payment_terms: e.target.value })} placeholder="Net 30, Due on Receipt…" disabled={!editable} className="rounded-sm" data-testid="so-payment-terms" /></div>
          <div><Label>Ship Date</Label><Input type="date" value={form.requested_ship_date} onChange={(e) => setForm({ ...form, requested_ship_date: e.target.value })} disabled={!editable} className="rounded-sm" data-testid="so-ship-date" /></div>
          <div><Label>Ship Via</Label><Input value={form.shipping_method} onChange={(e) => setForm({ ...form, shipping_method: e.target.value })} placeholder="FedEx Ground, UPS Overnight…" disabled={!editable} className="rounded-sm" data-testid="so-shipping-method" /></div>
          <div><Label>Customer PO #</Label><Input value={form.po_reference} onChange={(e) => setForm({ ...form, po_reference: e.target.value })} disabled={!editable} className="rounded-sm mono" data-testid="so-po-ref" /></div>
          <div><Label>Tracking #</Label><Input value={form.tracking_number} onChange={(e) => setForm({ ...form, tracking_number: e.target.value })} disabled={!editable} className="rounded-sm mono" data-testid="so-tracking" /></div>
          <div><Label>Tax Rate (0-1)</Label><Input type="number" step="0.01" min="0" max="1" value={form.tax_rate} onChange={(e) => setForm({ ...form, tax_rate: e.target.value })} disabled={!editable} className="rounded-sm mono" data-testid="so-tax-rate" /></div>
          <div><Label>Sales Rep</Label><Input value={form.salesperson_name} onChange={(e) => setForm({ ...form, salesperson_name: e.target.value })} disabled={!editable} className="rounded-sm" data-testid="so-salesperson" /></div>
          <div><Label>Incoterms</Label><Input value={form.incoterms} onChange={(e) => setForm({ ...form, incoterms: e.target.value })} placeholder="FOB Origin, DDP, EXW…" disabled={!editable} className="rounded-sm" data-testid="so-incoterms" /></div>
          <div className="col-span-2 md:col-span-2"><Label>Message on Invoice (prints on PDF)</Label><Textarea rows={2} value={form.message_on_invoice} onChange={(e) => setForm({ ...form, message_on_invoice: e.target.value })} disabled={!editable} className="rounded-sm" data-testid="so-message-invoice" /></div>
          <div className="col-span-2 md:col-span-2"><Label>Memo (internal only)</Label><Textarea rows={2} value={form.memo} onChange={(e) => setForm({ ...form, memo: e.target.value })} disabled={!editable} className="rounded-sm" data-testid="so-memo" /></div>
        </CardContent>
      </Card>

      <Card className="industrial-card">
        <CardHeader className="border-b border-slate-100 pb-3 flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base">Line Items</CardTitle>
          {editable && <Button size="sm" variant="outline" onClick={addLine} className="rounded-sm" data-testid="so-add-line-btn"><Plus className="w-3.5 h-3.5 mr-1" /> Add Line</Button>}
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Item SKU</TableHead>
              <TableHead>Product</TableHead>
              <TableHead>Description *</TableHead>
              <TableHead>Service Date</TableHead>
              <TableHead>Class</TableHead>
              <TableHead className="text-right">Qty</TableHead>
              <TableHead>UOM</TableHead>
              <TableHead className="text-right">Rate</TableHead>
              <TableHead className="text-right">Disc %</TableHead>
              <TableHead className="text-center">Tax</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              {editable && <TableHead></TableHead>}
            </TableRow></TableHeader>
            <TableBody>
              {form.lines.map((ln, i) => {
                const lt = (Number(ln.quantity || 0) * Number(ln.unit_price || 0) * (1 - Number(ln.discount_pct || 0) / 100)).toFixed(2);
                return (
                  <TableRow key={ln.id} data-testid={`so-line-${i}`}>
                    <TableCell className="min-w-[160px]">
                      <SearchPicker value={ln.item_sku || ''} options={itemOptions} onChange={(v) => fillLineFromItem(i, v)} disabled={!editable || !!ln.product_type_id} placeholder="— pick item —" testId={`so-line-item-${i}`} />
                    </TableCell>
                    <TableCell className="min-w-[160px]">
                      <SearchPicker value={ln.product_type_id || ''} options={productOptions} onChange={(v) => fillLineFromProduct(i, v)} disabled={!editable || !!ln.item_sku} placeholder="— pick product —" testId={`so-line-product-${i}`} />
                    </TableCell>
                    <TableCell className="min-w-[220px]"><Input value={ln.description} onChange={(e) => updateLine(i, { description: e.target.value })} disabled={!editable} className="rounded-sm text-xs" data-testid={`so-line-desc-${i}`} /></TableCell>
                    <TableCell className="w-32"><Input type="date" value={ln.service_date || ''} onChange={(e) => updateLine(i, { service_date: e.target.value })} disabled={!editable} className="rounded-sm text-xs" data-testid={`so-line-service-date-${i}`} /></TableCell>
                    <TableCell className="w-28"><Input value={ln.class_name || ''} onChange={(e) => updateLine(i, { class_name: e.target.value })} disabled={!editable} className="rounded-sm text-xs" placeholder="Class" data-testid={`so-line-class-${i}`} /></TableCell>
                    <TableCell className="text-right w-20"><Input type="number" min="0" step="0.01" value={ln.quantity} onChange={(e) => updateLine(i, { quantity: e.target.value })} disabled={!editable} className="rounded-sm text-xs text-right mono" data-testid={`so-line-qty-${i}`} /></TableCell>
                    <TableCell className="w-16"><Input value={ln.uom || ''} onChange={(e) => updateLine(i, { uom: e.target.value })} disabled={!editable} className="rounded-sm text-xs" /></TableCell>
                    <TableCell className="text-right w-24"><Input type="number" step="0.01" value={ln.unit_price} onChange={(e) => updateLine(i, { unit_price: e.target.value })} disabled={!editable} className="rounded-sm text-xs text-right mono" data-testid={`so-line-price-${i}`} /></TableCell>
                    <TableCell className="text-right w-20"><Input type="number" min="0" max="100" step="0.01" value={ln.discount_pct || 0} onChange={(e) => updateLine(i, { discount_pct: e.target.value })} disabled={!editable} className="rounded-sm text-xs text-right mono" data-testid={`so-line-disc-${i}`} /></TableCell>
                    <TableCell className="text-center w-14"><Checkbox checked={ln.taxable !== false} onCheckedChange={(v) => updateLine(i, { taxable: !!v })} disabled={!editable} data-testid={`so-line-taxable-${i}`} /></TableCell>
                    <TableCell className="text-right mono font-semibold">{Number(lt) < 0 ? `-$${Math.abs(lt).toFixed(2)}` : `$${lt}`}</TableCell>
                    {editable && <TableCell className="w-10"><Button variant="ghost" size="icon" onClick={() => removeLine(i)} className="text-red-600 h-7 w-7"><X className="w-3.5 h-3.5" /></Button></TableCell>}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card className="industrial-card">
        <CardContent className="p-4 flex items-end justify-between gap-6 flex-wrap">
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-xs" data-testid="so-totals-grid">
            <div><Label className="text-[10px]">Invoice Discount %</Label><Input type="number" min="0" max="100" step="0.01" value={form.invoice_level_discount_pct || 0} onChange={(e) => setForm({ ...form, invoice_level_discount_pct: e.target.value, invoice_level_discount_amount: 0 })} disabled={!editable} className="rounded-sm h-8 w-28 text-right mono" data-testid="so-invoice-disc-pct" /></div>
            <div><Label className="text-[10px]">Invoice Discount $</Label><Input type="number" min="0" step="0.01" value={form.invoice_level_discount_amount || 0} onChange={(e) => setForm({ ...form, invoice_level_discount_amount: e.target.value, invoice_level_discount_pct: 0 })} disabled={!editable} className="rounded-sm h-8 w-28 text-right mono" data-testid="so-invoice-disc-amt" /></div>
            <div className="col-span-2"><Label className="text-[10px]">Deposit $</Label><Input type="number" min="0" step="0.01" value={form.deposit_amount || 0} onChange={(e) => setForm({ ...form, deposit_amount: e.target.value })} disabled={!editable} className="rounded-sm h-8 w-28 text-right mono" data-testid="so-deposit" /></div>
          </div>
          <div className="text-sm space-y-1 min-w-[260px]" data-testid="so-totals">
            <div className="flex justify-between"><span>Subtotal</span><span className="mono font-semibold">${subtotal.toFixed(2)}</span></div>
            {discountAmount > 0 && <div className="flex justify-between text-slate-500"><span>Discount</span><span className="mono">− ${discountAmount.toFixed(2)}</span></div>}
            <div className="flex justify-between text-slate-500"><span>Taxable</span><span className="mono">${taxableAfter.toFixed(2)}</span></div>
            <div className="flex justify-between"><span>Tax ({(Number(form.tax_rate || 0) * 100).toFixed(2)}%)</span><span className="mono font-semibold">${taxAmount.toFixed(2)}</span></div>
            <div className="flex justify-between border-t pt-1"><span className="font-semibold">Total</span><span className="mono font-bold">${total.toFixed(2)}</span></div>
            {Number(form.deposit_amount || 0) > 0 && <div className="flex justify-between text-slate-500"><span>Deposit</span><span className="mono">− ${Number(form.deposit_amount).toFixed(2)}</span></div>}
            {paymentsTotal > 0 && <div className="flex justify-between text-slate-500"><span>Payments</span><span className="mono" data-testid="so-totals-payments">− ${paymentsTotal.toFixed(2)}</span></div>}
            <div className="flex justify-between bg-slate-900 text-white px-2 py-1.5 rounded-sm"><span className="font-semibold">Balance Due</span><span className="mono font-bold text-base" data-testid="so-balance-due">${balanceDue.toFixed(2)}</span></div>
          </div>
          {editable && (
            <Button onClick={save} disabled={saving} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="so-save-btn">
              <Save className="w-4 h-4 mr-1" /> {saving ? 'Saving…' : (isNew ? 'Create Draft' : 'Save Changes')}
            </Button>
          )}
        </CardContent>
      </Card>
      {so && <SoPaymentsCard api={api} soId={id} soStatus={so.status} onTotals={(t) => setSo(prev => ({ ...prev, ...t }))} />}
      {kitFor && <ProductKitDialog product={kitFor.product} items={items} unitQty={kitFor.qty} onConfirm={addKitLines} onClose={() => setKitFor(null)} />}
      {!isNew && so && <DocHistoryPanel entityType="SalesOrder" docId={id} refreshKey={`${so.status}-${so.updated_at}`} />}
    </div>
  );
};

export default SalesOrderDetailPage;
